import { ProductDetailsState } from '../product-details/types';
import { useEffect, useState } from 'react';
import AxiosHelper from 'services/AxiosHelper';
import { ServerData } from '../../../services/ServerData';
import { getPartialProductData } from '../product-details/data';

type ViewTracked = {
	lastViewed: number;
};

type SparseProductDetailsState = Pick<ProductDetailsState, 'id' | 'assets' | 'pricingLines' | 'displayPricing'> &
	Partial<Pick<ProductDetailsState, 'summary'>>;

const sparseProductDetailsStateKeys: (keyof SparseProductDetailsState)[] = [
	'summary',
	'assets',
	'pricingLines',
	'displayPricing',
];

/**
 * Recently viewed product
 */
export type RecentlyViewedProduct = SparseProductDetailsState & ViewTracked;

/**
 * Use recently viewed products hook params
 */
interface UseRecentlyViewedProductsParams {
	currentProduct?: ProductDetailsState | null;
	active?: boolean;
}

/**
 * Use recently viewed products context
 */
interface RecentlyViewedProductsContext {
	products: RecentlyViewedProduct[];
	timestamp: number | null;
}

class RecentlyViewedProductsRepository {
	/**
	 * Recently viewed product local storage key name
	 */
	private keyName: string = 'RocRecentlyViewedProductsContext';

	/**
	 * Get all recently viewed products from the local storage, including those that may no longer be valid.
	 */
	public getAllMayIncludeInvalid(): RecentlyViewedProduct[] {
		let context: RecentlyViewedProductsContext | null = null;
		try {
			const stateStr = localStorage.getItem(this.keyName);
			if (stateStr !== null) {
				context = JSON.parse(stateStr);
				if (context && context.timestamp) {
					const expirationTime = 1000 * 60 * 60 * 24 * 64; // 60 days in milliseconds
					if (new Date().valueOf() - context.timestamp >= expirationTime) {
						localStorage.removeItem(this.keyName); // remove data because it has expired
						context = null;
					}
				}
			}
		} catch (error) {
			console.warn('Error when getting recently viewed products from local storage.', error);
		}

		if (!context || context.products === null) {
			return new Array<RecentlyViewedProduct>();
		}

		return context.products;
	}

	/**
	 * Get all recently viewed products from the local storage, except those that the server deems are invalid.
	 */
	public async getAllValidOnly(): Promise<RecentlyViewedProduct[]> {
		const validProducts = await this.filterOutInvalid(this.getAllMayIncludeInvalid());

		this.setAll(validProducts);

		return validProducts;
	}

	/**
	 * Filter out invalid products from the passed in list by querying the server.
	 */
	private async filterOutInvalid(products: RecentlyViewedProduct[]) {
		try {
			const now: number = new Date().valueOf();
			// Obtain timespan from settings; default to 1 hour in milliseconds.
			const timeSpan: number = ServerData.MinimumTimeBeforeRecentlyViewedProductsAreValidated || 60 * 60 * 1000;

			const outsideTimeSpan: RecentlyViewedProduct[] = products.filter((p) => now - p.lastViewed > timeSpan);
			const withinTimeSpan: RecentlyViewedProduct[] = products.filter((p) => now - p.lastViewed <= timeSpan);

			if (outsideTimeSpan.length > 0) {
				const validProductIds: string[] = (
					await AxiosHelper.get('/ajax/commerce/products/recently-viewed/filter-out-invalid', {
						params: { productIds: outsideTimeSpan.map((product) => product.id) },
					})
				).data.map((id) => id.toLowerCase());

				const validOutsideTimeSpan: RecentlyViewedProduct[] = outsideTimeSpan.filter((product) =>
					validProductIds.includes(product.id.toLowerCase()),
				);

				for (const product of validOutsideTimeSpan) {
					product.lastViewed = now;
				}

				return withinTimeSpan.concat(validOutsideTimeSpan);
			}
		} catch (error) {
			console.error(`An error occurred while filtering out invalid products: ${error.message}`);
		}

		return products;
	}

	/**
	 * Update recently viewed products state in the local storage
	 */
	public setAll(products: RecentlyViewedProduct[]) {
		const context: RecentlyViewedProductsContext = {
			products,
			timestamp: new Date().valueOf(),
		};
		localStorage.setItem(this.keyName, JSON.stringify(context));
	}
}

const repository = new RecentlyViewedProductsRepository();

/**
 * Custom hook for recently viewed products
 */
export default function useRecentlyViewedProducts({
	currentProduct,
	active = true,
}: UseRecentlyViewedProductsParams): RecentlyViewedProduct[] {
	const maxPersistedProducts = 5;
	const [recentlyViewedProducts, setRecentlyViewedProducts] = useState<RecentlyViewedProduct[]>([]);

	/** Retrieve the partial product state for an arbitrary product */
	async function getProductStateAsync(productId: string) {
		try {
			const productState = await getPartialProductData({
				id: productId,
				missingKeys: sparseProductDetailsStateKeys,
			});
			console.debug('Product state retrieved:', productState);

			if (productState != null) {
				const recentlyViewedProduct: RecentlyViewedProduct = {
					...productState,
					id: productId,
					lastViewed: new Date().valueOf(),
				};

				return recentlyViewedProduct;
			}
		} catch (error) {
			console.error(error);
		}

		console.error('Failed to retrieve the product with the given id:', productId);
		return null;
	}

	useEffect(() => {
		if (!active) {
			return;
		}
		(async () => {
			let recentlyViewedProductDtos = await repository.getAllValidOnly();

			// process current product only when it was fully loaded w/o any errors
			if (currentProduct != null) {
				const currentProductDto: RecentlyViewedProduct = {
					id: currentProduct.id,
					summary: currentProduct.summary,
					assets: currentProduct.assets,
					pricingLines: currentProduct.pricingLines,
					displayPricing: currentProduct.displayPricing,
					lastViewed: new Date().valueOf(),
				};

				// If the product is already in the recently review list, remove it because it will be reinserted at the end of this method in order to refresh it with up to date data.
				const currentProductIndex = recentlyViewedProductDtos.findIndex(
					(item) => item.id === currentProductDto.id,
				);
				if (currentProductIndex > -1) {
					recentlyViewedProductDtos.splice(currentProductIndex, 1);
				}

				// If the product doesn't have a parent, add it to recently viewed.
				// If the user ended up following a direct link to a simple with a parent, it will not be added to recently viewed despite this being undesirable.
				if (currentProductDto.summary != null) {
					const groupId = currentProductDto.summary.productGroupId;
					if (groupId == null) {
						recentlyViewedProductDtos.unshift(currentProductDto); // insert current item at the first place
					} else {
						const groupIndex = recentlyViewedProductDtos.findIndex((item) => item.id === groupId);
						if (groupIndex > 0) {
							// Move the group to the front of the list but don't reload it.
							const groupProducts = recentlyViewedProductDtos.splice(groupIndex, 1);
							recentlyViewedProductDtos.unshift(groupProducts[0]);
						} else if (groupIndex === -1) {
							// Attempt to retrieve enough product state information to insert a recently viewed product entry for the group.
							const currentGroupDto = await getProductStateAsync(groupId);
							if (currentGroupDto != null) {
								recentlyViewedProductDtos.unshift(currentGroupDto);
							}
						}
					}
				}

				recentlyViewedProductDtos = recentlyViewedProductDtos.slice(0, maxPersistedProducts);
				repository.setAll(recentlyViewedProductDtos);
				recentlyViewedProductDtos = recentlyViewedProductDtos.filter(
					(p) => p.id !== currentProductDto.id && p.id !== currentProductDto.summary?.productGroupId,
				); // exclude current product and/or group from being rendered
			}

			setRecentlyViewedProducts(recentlyViewedProductDtos);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentProduct && currentProduct.id, active]);

	return recentlyViewedProducts;
}
